import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout, Spin } from 'antd';
import { QueryClientProvider, QueryClient } from 'react-query';
import { useAuthStore } from './store';
import { authAPI } from './api/endpoints';
import Sidebar from './components/Sidebar';
import Header from './components/Header';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import InvoicesPage from './pages/InvoicesPage';
import PaymentsPage from './pages/PaymentsPage';
import PortfolioPage from './pages/PortfolioPage';
import MailingPage from './pages/MailingPage';
import RulesPage from './pages/RulesPage';
import ProfilePage from './pages/ProfilePage';
import UsersPage from './pages/UsersPage';

import './App.scss';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5,
    },
  },
});

function App() {
  const { isAuthenticated, user, setUser } = useAuthStore();
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated) {
        try {
          const response = await authAPI.profile();
          setUser(response.data);
        } catch (error) {
          console.error('Error fetching profile:', error);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  const role = user?.role;
  const isAdmin    = role === 'admin';
  const isOperator = role === 'operator';

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {isAuthenticated ? (
          <Layout className="layout">
            <Sidebar />
            <Layout>
              <Header />
              <Layout.Content className="content">
                <Routes>
                  {/* Rutas disponibles para todos los roles */}
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/profile" element={<ProfilePage />} />

                  {/* Rutas para admin y operador */}
                  {(isAdmin || isOperator) && (
                    <>
                      <Route path="/customers" element={<CustomersPage />} />
                      <Route path="/invoices" element={<InvoicesPage />} />
                      <Route path="/payments" element={<PaymentsPage />} />
                      <Route path="/portfolio" element={<PortfolioPage />} />
                    </>
                  )}

                  {/* Rutas exclusivas para admin */}
                  {isAdmin && (
                    <>
                      <Route path="/mailing" element={<MailingPage />} />
                      <Route path="/rules" element={<RulesPage />} />
                      <Route path="/users" element={<UsersPage />} />
                    </>
                  )}

                  {/* Cualquier ruta no registrada → dashboard */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout.Content>
            </Layout>
          </Layout>
        ) : (
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
